import ComposeBody from './ComposeBody';
import ComposeBottom from './ComposeBottom';
import ComposeTop from './ComposeTop';
import { DarkModeContext } from 'owa-theme';
import { observer } from 'mobx-react';
import { lazyUpdateRibbonWidth } from 'owa-editor-ribbonplugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import { keydownHandler } from 'owa-hotkeys';
import { addInitialAction, InitialActions } from 'owa-initial-actions';
import { lazyCreateMailComposeAdapter, runAfterNotificationSetup } from 'owa-mail-addins';
import { lazyHasInlineImage } from 'owa-mail-addins';
import shouldAddinsPopulate from 'owa-mail-addins/lib/utils/shouldAddinsPopulate';
import clearAttachmentFilesToUpload from 'owa-mail-compose-store/lib/actions/clearAttachmentFilesToUpload';
import closeCompose from 'owa-mail-compose-store/lib/actions/closeCompose';
import discardCompose from 'owa-mail-compose-store/lib/actions/discardCompose';
import trySaveMessage, { isSaving } from 'owa-mail-compose-store/lib/actions/trySaveMessage';
import trySendMessage from 'owa-mail-compose-store/lib/actions/trySendMessage';
import StickyDockingPlugin from 'owa-mail-compose-store/lib/plugins/docking/StickyDockingPlugin';
import ComposeViewState from 'owa-mail-compose-store/lib/store/schema/ComposeViewState';
import addAttachments from 'owa-mail-compose-store/lib/utils/addAttachments';
import { isPublicFolderComposeViewState } from 'owa-mail-compose-store/lib/utils/createPublicFolderComposeViewState';
import { isGroupScenario, isViewStateDirty } from 'owa-mail-compose-store/lib/utils/viewStateUtils';
import { resetFocus } from 'owa-mail-focus-manager';
import { getCommands } from 'owa-mail-hotkeys';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import * as React from 'react';
import {
    registerComposeFocusManager,
    unregisterComposeFocusManager,
} from '../utils/composeFocusManager';
import ComposePlugins, {
    findComposePlugins,
} from 'owa-mail-compose-store/lib/plugins/ComposePlugins';
import getAutoFocusControl, {
    ComposeControl,
} from 'owa-mail-compose-store/lib/utils/getAutoFocusControl';
import {
    lazyInitializeAddinsForItem,
    lazyDeinitializeAddinsForItem,
    lazyCloseTaskPaneAddinCommand,
    MessageComposeAdapter,
    getComposeHostItemIndex,
} from 'owa-addins-core';

const styles = require('./Compose.scss');
const classNames = require('classnames');

export interface ComposeProps {
    viewState: ComposeViewState;
    initialActions: InitialActions;
    className?: string;
    senderPersona?: JSX.Element;
    onUnmountFocusCallback?: () => void;
}

const AUTO_SAVE_INTERVAL = 30000;
const AUTO_SAVE_INTERNAL_GROUPS = 300000; // 5 minutes
const FOCUS_TIMER = 10;

const enum ResizeEventState {
    NotSubscribed,
    Subscribed,
    Unmounted,
}

/**
 * Scope for artificial resize events.
 * Empty string is the default scope.
 *
 * When a FlexPane mounts or unmounts, the
 * artificial resize it fires is on this scope.
 **/
const RESIZE_EVENT_SCOPE: string = '';

@observer
export default class Compose extends React.Component<ComposeProps, {}> {
    private top: ComposeTop;
    private body: ComposeBody;
    private bottom: ComposeBottom;
    private autoSaveDraftTimer: number;
    private rootDiv: HTMLDivElement;
    private setFocusTimer: number;
    private resizeTimer: number;
    private plugins: ComposePlugins;
    private resizeEventState = ResizeEventState.NotSubscribed;

    constructor(props: ComposeProps) {
        super(props);
        const { viewState } = this.props;
        this.plugins = findComposePlugins(viewState);
    }

    componentDidMount() {
        this.autoSaveDraftTimer = window.setInterval(
            this.autoSaveDraft,
            isGroupScenario(this.props.viewState) ? AUTO_SAVE_INTERNAL_GROUPS : AUTO_SAVE_INTERVAL
        );
        registerComposeFocusManager(this);
        lazySubscribeToResizeEvent.import().then(subscribeToResizeEvent => {
            if (this.resizeEventState == ResizeEventState.NotSubscribed) {
                subscribeToResizeEvent(
                    this.onResize,
                    RESIZE_EVENT_SCOPE,
                    this.rootDiv.ownerDocument.defaultView
                );
                this.resizeEventState = ResizeEventState.Subscribed;
            }
        });
        addInitialAction(this.props.initialActions, () => {
            this.onResize();
        });
        this.focus();
        if (shouldAddinsPopulate(this.props.viewState)) {
            lazyCloseTaskPaneAddinCommand.import().then(closeTaskPaneAddinCommand => {
                closeTaskPaneAddinCommand();
            });

            lazyCreateMailComposeAdapter.import().then(createMailComposeAdapter => {
                const adapter: MessageComposeAdapter = createMailComposeAdapter(
                    this.props.viewState
                );
                lazyInitializeAddinsForItem.import().then(initializeAddinsForItem => {
                    initializeAddinsForItem(
                        getComposeHostItemIndex(this.props.viewState.composeId),
                        adapter
                    );
                });
            });
        }

        runAfterNotificationSetup(() => {
            const viewState = this.props.viewState;
            addAttachments(viewState, viewState.attachmentFilesToUpload).then(() => {
                lazyHasInlineImage
                    .import()
                    .then(hasInlineImage => {
                        if (
                            this.plugins.addinPlugin &&
                            hasInlineImage(viewState.attachmentFilesToUpload)
                        ) {
                            // TODO: VSO 26855: addinPluggin is not guaranteed to have been initialized
                            this.plugins.addinPlugin.triggerContentChangedByAddinEvent();
                        }
                    })
                    .then(() => {
                        clearAttachmentFilesToUpload(viewState);
                    });
            });
        });
    }

    componentWillUnmount() {
        this.cancelResizeTimer();
        unregisterComposeFocusManager(this);
        window.clearInterval(this.autoSaveDraftTimer);
        if (this.resizeEventState == ResizeEventState.Subscribed) {
            lazyUnsubscribeFromResizeEvent.importAndExecute(this.onResize, RESIZE_EVENT_SCOPE);
        }
        this.resizeEventState = ResizeEventState.Unmounted;

        if (this.props.onUnmountFocusCallback) {
            this.props.onUnmountFocusCallback();
        } else {
            resetFocus();
        }

        isFeatureEnabled('rp-addins') &&
            lazyDeinitializeAddinsForItem.import().then(deinitializeAddinsForItem => {
                deinitializeAddinsForItem(getComposeHostItemIndex(this.props.viewState.composeId));
            });
    }

    render() {
        const { className, viewState } = this.props;
        return (
            <DarkModeContext.Provider value={viewState.isDarkMode}>
                <div
                    className={classNames(className, styles.composeOuterContainer)}
                    ref={this.rootDivRefCallback}>
                    <div className={!viewState.isDarkMode && styles.composeInnerContainer}>
                        <ComposeTop
                            {...this.props}
                            plugins={this.plugins}
                            ref={ref => (this.top = ref)}
                        />
                        <ComposeBody
                            {...this.props}
                            plugins={this.plugins}
                            ref={ref => (this.body = ref)}
                        />
                        {/*
                            Workaround VSO 8885: Compose: JS error when set indent then do another formatting in Edge
                            Add an empty DIV under composeBodyPart to avoid composeBottomPart got broken
                            TODO: (VSO 8885) Remove this workaround once we have an official fix
                        */}
                        <div />
                        <ComposeBottom
                            ref={ref => (this.bottom = ref)}
                            isHidden={viewState.docking.isBottomDocking}
                            {...this.props}
                            plugins={this.plugins}
                        />
                        <div className={styles.bottomBorder} ref={this.setStickyBottomBorder} />
                    </div>
                    {this.props.children}
                </div>
            </DarkModeContext.Provider>
        );
    }

    /**
     * Callback when the compose pane gains focus from focus manager
     */
    public focus() {
        if (!this.setFocusTimer) {
            // Always do focus in async mode because this function can be called from an action,
            // and at that time the component may not be updated by React yet.
            // Use setTimeout rather than RAF to allow browser finish a reflow first and user can
            // see the view, the we do focus to avoid a force reflow.
            this.setFocusTimer = window.setTimeout(() => {
                if (this.body && this.top) {
                    const controlToFocus = getAutoFocusControl(this.props.viewState);

                    if (controlToFocus == ComposeControl.Editor) {
                        this.body.trySetFocus();
                    } else {
                        this.top.trySetFocus(controlToFocus);
                    }
                }
                this.setFocusTimer = 0;
            }, FOCUS_TIMER);
        }
    }

    public getRootDiv() {
        return this.rootDiv;
    }

    private setStickyBottomBorder = (ref: HTMLDivElement) => {
        const stickyDockingPlugin = this.plugins
            ? (this.plugins.dockingPlugin as StickyDockingPlugin)
            : null;
        if (this.props.viewState.docking.useStickyDocking && stickyDockingPlugin) {
            stickyDockingPlugin.setStickyBottomBorder(ref);
        }
    };

    private rootDivRefCallback = (div: HTMLDivElement) => {
        if (this.plugins.dockingPlugin) {
            this.plugins.dockingPlugin.setInitialVisiblePart(div);
        }

        this.rootDiv = div;
    };

    private autoSaveDraft = () => {
        const { viewState } = this.props;
        this.plugins.contentOperationPlugin.updateContentToViewState();

        // Don't auto save if item creation is in progress
        if (!isSaving(viewState) && isViewStateDirty(viewState)) {
            trySaveMessage(viewState, true /*isAutoSave*/);
        }
    };

    private cancelResizeTimer() {
        if (this.resizeTimer) {
            window.clearTimeout(this.resizeTimer);
            this.resizeTimer = 0;
        }
    }

    private onResize = () => {
        this.cancelResizeTimer();
        this.resizeTimer = window.setTimeout(() => {
            this.resizeTimer = 0;
            if (this.rootDiv) {
                const clientWidth = this.rootDiv.clientWidth;
                lazyUpdateRibbonWidth.importAndExecute(this.props.viewState.ribbon, clientWidth);
            }
            if (this.bottom) {
                this.bottom.resize();
            }
        }, 1);
    };

    @keydownHandler(getCommands().saveDraft)
    saveDraft() {
        this.plugins.contentOperationPlugin.updateContentToViewState();
        trySaveMessage(this.props.viewState);
    }

    @keydownHandler(getCommands().sendMail)
    sendMessage() {
        this.plugins.contentOperationPlugin.updateContentToViewState();
        if (isPublicFolderComposeViewState(this.props.viewState)) {
            trySaveMessage(this.props.viewState);
            closeCompose(this.props.viewState);
        } else {
            trySendMessage(this.props.viewState);
        }
    }

    @keydownHandler(
        getCommands().discardDraft,
        true /*stopPropagation*/,
        true /*preventDefault*/,
        true /*allowHotkeyOnTextFields*/
    )
    discardDraft() {
        const plugins = findComposePlugins(this.props.viewState);
        if (plugins && plugins.ribbonPlugin) {
            plugins.ribbonPlugin.workaroundFocusIssueForFirefox();
        }
        discardCompose(this.props.viewState);
    }
}
